import './TableFrame.scss'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { clx } from '../clx'
import { TableFrameContext, type TableFrameColumn, type TableFrameState } from './context'

export interface TableFrameProps {
  /** Names the table's scroll region while it scrolls (usually the list title). */
  label: string
  /** The DataTable, plus anything that scrolls with it (an infinite list's sentinel). */
  children: ReactNode
  /** Above the table, inside the frame: the FilterBar. Never scrolls. */
  toolbar?: ReactNode
  /** Below the table, inside the frame: Pagination, a load-more row. Never scrolls. */
  footer?: ReactNode
  /**
   * Who scrolls the rows (ADR 0025):
   * - `page` (default): the page; the frame scrolls only sideways, and only
   *   when the table cannot fit;
   * - `frame`: from `tablet` the frame fills its parent's height and scrolls
   *   both ways itself, so the header, the toolbar, the footer and the
   *   horizontal scrollbar stay in view. The parent bounds the height (a
   *   page root with `data-shell-fill`).
   */
  scroll?: 'page' | 'frame'
  /** `plain` inside a surface that already frames it (a titled Card). Default `card`. */
  variant?: 'card' | 'plain'
  className?: string
}

/** The inherited property DataTable's filtered headers read their minimum from (ADR 0023). */
const RELAXED = '--data-table-filtered-min'

/**
 * Once the filtered minimum is dropped, it is tried again only when the
 * viewport has grown by this much, px. Dropping it changes how the cells wrap
 * and so the table's height; retrying at the same width could flip it back
 * and forth.
 */
const RELAX_SLACK = 32

/**
 * The nearest ancestor that scrolls vertically. `hidden` does not count: the
 * page-mode viewport clips that axis only because it scrolls sideways.
 */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
  }
  return null
}

/**
 * The frame around a DataTable (ADR 0025). It owns what the table and its
 * filters used to negotiate through the list:
 * - the scroller: sideways only when the table cannot fit (`page`), or both
 *   ways inside a height-bounded frame (`frame`);
 * - the sticky header's stuck state, against whichever box scrolls;
 * - the overflow check, which first drops the filtered-column minimum;
 * - the column positions for the toolbar's FilterBar, and the filtered
 *   columns back to the DataTable (through context).
 */
export function TableFrame({
  label,
  children,
  toolbar,
  footer,
  scroll = 'page',
  variant = 'card',
  className
}: TableFrameProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [columns, setColumns] = useState<TableFrameColumn[] | null>(null)
  const [filteredColumns, setFilteredColumns] = useState<string[]>([])
  const [isStuck, setStuck] = useState(false)
  const [isOverflowing, setOverflowing] = useState(false)
  const [isScrollable, setScrollable] = useState(false)
  const observerRef = useRef<ResizeObserver | null>(null)
  const observedTable = useRef<Element | null>(null)
  const measureRef = useRef<() => void>(() => undefined)
  const hasFilters = filteredColumns.length > 0

  // Overflow, scrollability and column positions, in one pass so the
  // positions always describe the layout that was kept.
  useLayoutEffect(() => {
    const viewport = viewportRef.current
    if (!viewport || typeof ResizeObserver === 'undefined') return
    let last = ''
    // The viewport width at which the minimum was dropped, if it is.
    let relaxedAt: number | null = null
    const measure = () => {
      const table = viewport.querySelector('table')
      if (!table) return
      const width = viewport.clientWidth
      const fits = () => table.offsetWidth <= width + 1
      // Filtered columns get room for their filter, unless the table would
      // then not fit: drop the minimum (ADR 0023) until there is clearly
      // more room.
      if (relaxedAt === null || width >= relaxedAt + RELAX_SLACK) {
        viewport.style.removeProperty(RELAXED)
        relaxedAt = null
        if (!fits() && hasFilters) {
          viewport.style.setProperty(RELAXED, '0px')
          relaxedAt = width
        }
      }
      const overflowing = !fits()
      setOverflowing(overflowing)
      setScrollable(overflowing || viewport.scrollHeight > viewport.clientHeight + 1)
      const next = overflowing
        ? null
        : Array.from(table.querySelectorAll<HTMLElement>('thead th[data-column-key]')).map((th) => {
            const rect = th.getBoundingClientRect()
            const pad = Number.parseFloat(getComputedStyle(th).paddingInlineStart) || 0
            return { key: th.dataset.columnKey ?? '', left: rect.left + pad, right: rect.right }
          })
      const serialized = JSON.stringify(next)
      if (serialized !== last) {
        last = serialized
        setColumns(next)
      }
    }
    const observer = new ResizeObserver(measure)
    // Each run starts unrelaxed (the filtered columns changed).
    viewport.style.removeProperty(RELAXED)
    const table = viewport.querySelector('table')
    observer.observe(viewport)
    if (table) observer.observe(table)
    observerRef.current = observer
    observedTable.current = table
    measureRef.current = measure
    measure()
    return () => {
      observer.disconnect()
      observerRef.current = null
      observedTable.current = null
    }
  }, [hasFilters])

  // The table can mount after the frame, or be replaced: keep observing the
  // one that is there now.
  useLayoutEffect(() => {
    const table = viewportRef.current?.querySelector('table') ?? null
    const observer = observerRef.current
    if (!observer || table === observedTable.current) return
    if (observedTable.current) observer.unobserve(observedTable.current)
    if (table) observer.observe(table)
    observedTable.current = table
    measureRef.current()
  })

  // Stuck: the header cells are pinned while their thead has scrolled past,
  // in the frame (`frame`) or in the page (`page`). Purely visual.
  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const page = scrollParent(viewport)
    let frame = 0
    const measure = () => {
      frame = 0
      const cell = viewport.querySelector('thead th')
      const head = cell?.closest('thead')
      if (!cell || !head) return
      setStuck(head.getBoundingClientRect().top < cell.getBoundingClientRect().top - 1)
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    viewport.addEventListener('scroll', onScroll, { passive: true })
    page?.addEventListener('scroll', onScroll, { passive: true })
    measure()
    return () => {
      viewport.removeEventListener('scroll', onScroll)
      page?.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  const state = useMemo<TableFrameState>(
    () => ({ columns, filteredColumns, isStuck, setFilteredColumns }),
    [columns, filteredColumns, isStuck]
  )

  return (
    <TableFrameContext value={state}>
      <div
        className={clx(
          'table-frame',
          scroll === 'frame' && 'table-frame--is-filling',
          variant === 'plain' && 'table-frame--as-plain',
          className
        )}
        data-overflowing={isOverflowing || undefined}
      >
        {toolbar != null && <div className="table-frame__toolbar">{toolbar}</div>}
        <div
          ref={viewportRef}
          className="table-frame__viewport"
          // A region that scrolls must be reachable and named for keyboard users.
          {...(isScrollable && { role: 'region', 'aria-label': label, tabIndex: 0 })}
        >
          {children}
        </div>
        {footer != null && footer !== false && <div className="table-frame__footer">{footer}</div>}
      </div>
    </TableFrameContext>
  )
}
